/*
 * useDarkTimeWarning — live "this window runs past dark time" advisory (#1119).
 *
 * Warns WHILE the user is choosing a fixed-time window, rather than only after the observation has
 * been created. A window ending after the observatory's dark time gets truncated at dispatch (the
 * server clamps `utc_end_time` to the same ceiling), so without this the user commits a window and
 * discovers the truncation only from a run that stopped early — or, as on prod 2026-07-23, from a
 * run that imaged into twilight.
 *
 * The ceiling is FETCHED from GET /observatories/:id/dark-time-limit, never recomputed here: that
 * endpoint returns `observatoryDawnLimit(...)`, the very function the dispatcher clamps against.
 * Reimplementing dawn in the browser would give the user a second, quietly different answer.
 *
 * Returns `null` when there is nothing to say — not a fixed-time observation, no window end yet, no
 * observatory selected, the site opts out of dark-time constraints, or the window fits.
 */

import { useEffect, useState } from 'react';
import { apiService } from '../../../../services/apiService';
import { buildDarkTimeWarning } from '../../../../utils/darkTimeWarning';

/**
 * @param {object}  opts
 * @param {string}  opts.observatoryId  assigned observatory (the ceiling is site-specific)
 * @param {*}       opts.fixedTimeStart window start — anchors WHICH night's dawn is looked up
 * @param {*}       opts.fixedTimeEnd   window end — the value compared against the ceiling
 * @param {boolean} opts.enabled        false for non-fixed_time observations
 * @returns {string|null} the advisory message, or null
 */
export function useDarkTimeWarning({ observatoryId, fixedTimeStart, fixedTimeEnd, enabled = true } = {}) {
  const [limit, setLimit] = useState(null);

  // Anchor on the START: that identifies the night in question. Anchoring on `now` would look up
  // the wrong night for anything scheduled more than a few hours out.
  const anchor = fixedTimeStart ? new Date(fixedTimeStart).toISOString() : null;
  const validAnchor = anchor && !Number.isNaN(new Date(anchor).getTime()) ? anchor : null;

  useEffect(() => {
    let cancelled = false;
    if (!enabled || !observatoryId || !validAnchor) {
      setLimit(null);
      return undefined;
    }
    (async () => {
      try {
        const res = await apiService.getObservatoryDarkTimeLimit(observatoryId, validAnchor);
        if (!cancelled) setLimit(res?.data ?? null);
      } catch (err) {
        // Advisory only: a failed lookup must never block or disrupt the form. The server still
        // warns on save and still clamps at dispatch, so the user is not left unprotected.
        console.warn('Dark-time limit lookup failed (non-fatal):', err);
        if (!cancelled) setLimit(null);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, observatoryId, validAnchor]);

  if (!enabled || !limit?.dawn_limit || !fixedTimeEnd) return null;

  return buildDarkTimeWarning({
    fixedTimeEnd,
    dawnLimit: limit.dawn_limit,
    observatoryName: limit.observatory_name,
  });
}

export default useDarkTimeWarning;
